import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import take from 'lodash/take';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import {
  style,
  useFollowers,
  useStatisticsForAllTracks,
  User,
  useSocialInfo,
  useUserStats,
  useIsLoggedIn,
  Genre,
  Follower,
  axios,
  useQueryAwardsForUser,
} from '../../api';

import Loader from '../common/Loader';
import { RequestFeedbackButton, FollowButton } from '../common/Buttons/V2';
import {
  noDesktop,
  desktop,
  useIsDesktop,
  NoDesktop,
} from '../../style/constants';
import UserSocial from './UserSocial';
import { Heart, Play, Location } from '../common/Icons';
import { TextBold14, TextRegular14, Text } from '../common/Text';
import UserImage from '../common/UserImage';
import { GenreLink, ProfileLink } from '../common/Link';
import formatLocation from '../../utils/formatLocation';
import AwardCard from '../Awards/AwardCard';
import Flex from '../common/Flex';
import Box from '../common/Box';
import ProfileFollowersLabel from '../common/ProfileFollowersLabel';
import EditProfileButton from '../common/Buttons/V2/EditProfileButton';
import StatsButton from '../common/Buttons/V2/StatsButton';
import { ExpandableText } from '../common/ExpandableText';

const defaultGenreColor = {
  primary: style.techno_title,
  bg: style.techno_bg,
};

const GenreName = styled.div<{ color: string; bg: string }>`
  color: ${(p) => p.color};
  background: ${(p) => p.bg};
  text-transform: lowercase;
  font-size: 12px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 5px;
`;

const getGenreColor = (genreId: number) => {
  const genreColor = style.genreIdColorTable[genreId];

  return genreColor || defaultGenreColor;
};

const GenreList = ({ genres }: { genres?: Genre[] }) => {
  if (!(Array.isArray(genres) && genres.length > 0)) {
    return null;
  }

  return (
    <GenresRow style={{ marginBottom: 16 }}>
      {genres.map((genre) => (
        <GenreLink genreId={genre.id} key={genre.id}>
          <GenreName
            color={getGenreColor(genre.id).primary}
            bg={getGenreColor(genre.id).bg}
          >
            {genre.name}
          </GenreName>
        </GenreLink>
      ))}
    </GenresRow>
  );
};

const FollowersList = ({ followers }: { followers?: Follower[] }) => {
  if (!(Array.isArray(followers) && followers.length > 0)) {
    return null;
  }

  return (
    <FollowersRow style={{ marginBottom: 16 }}>
      {followers.map((follower) => (
        <ProfileLink profileId={follower.id} key={follower.id}>
          <UserImage src={follower.profile_photo} size={36} />
        </ProfileLink>
      ))}
    </FollowersRow>
  );
};

const MutualFollowers = ({ user }: { user: User }) => {
  const { t } = useTranslation('profile');
  const [{ total, records }, setState] = useState<{
    total: number;
    records: Array<Follower>;
  }>({ total: 0, records: [] });

  /**
   * Current `useFollowers` hook is caching results based on pagination
   * which we would need to extend and update caching based on addition param
   * `mutual=true` which makes things more complex.
   *
   * User stats for `total_mutual_followers` returns incorrect data
   * so we stick with using this query since it proved total number
   * in pagination.
   *
   */
  useEffect(() => {
    axios({
      method: 'GET',
      url: `follow/${user.id}`,
      params: { mutual: true, page: 1 },
    })
      .then(({ data: { data, pagination } }) => {
        setState({
          total: pagination.total,
          records: (data || []).slice(0, 6),
        });
      })
      .catch(() => null);
  }, [user.id]);

  if (!(Array.isArray(records) && records.length > 0)) {
    return null;
  }

  return (
    <MutualFollowersContainer>
      <MutualFollowersContainerList>
        {records.map((follower) => (
          <ProfileLink profileId={follower.id} key={follower.id}>
            <UserImage src={follower.profile_photo} size={28} />
          </ProfileLink>
        ))}
      </MutualFollowersContainerList>
      <div>
        <TextRegular14>{total}</TextRegular14>
        <GreyText style={{ marginLeft: 4 }}>
          {t('profile:mutualFollowers') as string}
        </GreyText>
      </div>
    </MutualFollowersContainer>
  );
};

const FlexedFollowButton = styled(FollowButton)`
  flex: 1;
  display: flex;
`;

const About = ({
  isReady,
  user,
  showFeedbackModal,
  current,
}: {
  isReady: boolean;
  user?: User;
  showFeedbackModal: () => void;
  current: boolean;
}) => {
  const { t } = useTranslation('profile');
  const loggedIn = useIsLoggedIn();

  const stats = useUserStats(user?.id);

  const [followers] = useFollowers(user?.id);
  const isDesktop = useIsDesktop();
  const { data: awards = [] } = useQueryAwardsForUser(user?.id);
  const { data: statistics } = useStatisticsForAllTracks('all');
  const { data: socialItems = [] } = useSocialInfo(user?.id);

  if (!isReady || !user) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  const {
    id,
    role,
    age,
    country,
    about,
    city,
    total_plays,
    total_followers,
    total_likes,
    genres,
    producer_roles,
  } = user;

  const plays = current ? statistics?.plays : total_plays;
  const likes = current ? statistics?.likes : total_likes;

  const showSocialLinks = socialItems.filter(Boolean).length > 0;

  const followersToShow = take(
    followers.filter((follower) => follower.following),
    6,
  );

  // ! If user is logged in and its not his profile
  const isCurrentProfile = !!(loggedIn && !current);

  const showMutualFollowers =
    isCurrentProfile && !!stats?.total_mutual_followers;

  /**
   * For label users we have way to different profile about section,
   * so lets make it clear.
   */
  if (role === 'label') {
    return (
      <WrapperProfileLabel>
        <GenreList genres={genres} />
        <NoDesktop>
          <ProfileLabelActionsContainer>
            {current ? (
              <Link href="/profile/edit">
                <span style={{ width: '100%' }}>
                  <EditProfileButton />
                </span>
              </Link>
            ) : (
              <FlexedFollowButton userId={id} />
            )}
            <ProfileFollowersLabel
              style={{ display: 'flex', flex: 1 }}
              followers={user.total_followers}
            />
          </ProfileLabelActionsContainer>
        </NoDesktop>
        {
          // ? Show the Mutual followers only if user is logged in, otherwise makes no sense, ant is not the current user.
          isCurrentProfile && <MutualFollowers user={user} />
        }
        {showSocialLinks && (
          <SocialContainer>
            <UserSocial user={user} />
          </SocialContainer>
        )}
      </WrapperProfileLabel>
    );
  }

  return (
    <Wrapper>
      <Row>
        <Flex flexDirection="column" width="100%">
          <Text fontWeight="bold" fontSize="14px">
            {t('profile:totalOnAllTracks')}
          </Text>
          <PlaysLikesRow style={{ marginBottom: 10 }}>
            {plays !== undefined && <Plays plays={plays} />}
            {likes !== undefined && <Likes likes={likes} />}
          </PlaysLikesRow>
        </Flex>
      </Row>
      <GenreList genres={genres} />
      <>
        <Row style={{ marginBottom: 6 }}>
          <GreyText style={{ textTransform: 'capitalize' }}>
            {[age, ...(producer_roles || []).map((roles) => roles.name)]
              .filter(Boolean)
              .join(', ')}
          </GreyText>
        </Row>
        <Row style={{ marginBottom: 18 }}>
          <Location
            size={14}
            color={style.grey_dark_new}
            style={{ marginLeft: -2, marginRight: 4 }}
          />
          <GreyText
            style={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {formatLocation(city, country?.name)}
          </GreyText>
        </Row>
        <Row style={{ paddingBottom: 16, marginBottom: 12 }} borderBottom>
          <ExpandableText>{about}</ExpandableText>
        </Row>
        {awards.length > 0 && (
          <Row>
            <Flex flexDirection="column" width="100%">
              <Text fontWeight="bold" fontSize="14px" mb="12px">
                {awards.length} {t('profile:awards')}
              </Text>
              <Flex
                flexDirection={['row', 'row', 'column']}
                overflowX={['scroll', 'scroll', 'auto']}
                width={['100vw', '100vw', '100%']}
                marginX={['-20px', '-20px', '0px']}
              >
                {awards.map((award) => (
                  <AwardWrapper>
                    <AwardCard award={award} compact />
                  </AwardWrapper>
                ))}
              </Flex>
            </Flex>
          </Row>
        )}
      </>

      <NoDesktop>
        <ButtonsRow style={{ flexDirection: 'column' }}>
          {current ? (
            <>
              <Link href="/profile/edit">
                <span style={{ width: '100%' }}>
                  <EditProfileButton />
                </span>
              </Link>
              <Link href="/statistics">
                <span style={{ width: '100%' }}>
                  <StatsButton />
                </span>
              </Link>
            </>
          ) : (
            <>
              <RequestFeedbackButton onClick={showFeedbackModal} />

              <FollowButton userId={id} />
            </>
          )}
        </ButtonsRow>
      </NoDesktop>

      <Row style={{ marginBottom: 14 }}>
        <>
          <TextRegular14>{total_followers}</TextRegular14>{' '}
          <GreyText style={{ marginLeft: 4 }}>
            {t('profile:followers')}
          </GreyText>
        </>
        <>
          <TextRegular14 style={{ marginLeft: 24 }}>
            {stats?.total_following}
          </TextRegular14>
          <GreyText style={{ marginLeft: 4 }}>
            {t('profile:following')}
          </GreyText>
        </>
        {!!(showMutualFollowers && !isDesktop) && (
          <div style={{ marginLeft: 'auto' }}>
            <TextRegular14>{stats?.total_mutual_followers}</TextRegular14>
            <GreyText style={{ marginLeft: 4 }}>{t('profile:mutual')}</GreyText>
          </div>
        )}
      </Row>
      <FollowersList followers={followersToShow} />
      {!!(showMutualFollowers && isDesktop) && (
        <Row style={{ marginBottom: 16 }}>
          <GreyText>
            {stats?.total_mutual_followers} {t('profile:mutualFollowers')}
          </GreyText>
        </Row>
      )}
      {showSocialLinks && (
        <Row style={{ height: 'auto', paddingTop: 20 }} borderTop>
          <UserSocial user={user} />
        </Row>
      )}
    </Wrapper>
  );
};

export default About;

const AwardWrapper = styled(Box)`
  margin-bottom: 18px;

  @media ${noDesktop} {
    &:last-child {
      padding-right: 20px;
    }
    padding-left: 20px;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  border-radius: 2px;

  @media ${desktop} {
    border-right: 1px solid ${style.grey_light_1_new};
    border-left: 1px solid ${style.grey_light_1_new};
    padding-bottom: 100px;
  }

  @media ${noDesktop} {
  }
`;

const WrapperProfileLabel = styled(Wrapper)`
  @media ${noDesktop} {
    margin-bottom: 22px;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
`;

const CounterWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  padding: 0.3rem 0rem;
  svg {
    margin-right: 8px;
  }

  @media ${noDesktop} {
    flex-basis: 50%;
    ${TextBold14}, ${TextRegular14} {
      font-size: 18px;
    }
  }
`;

const Row = styled.div<{ borderBottom?: boolean; borderTop?: boolean }>`
  display: flex;
  align-items: center;
  border-bottom: ${(p) =>
    p.borderBottom ? `1px solid ${style.grey_light_1_new}` : 'none'};

  border-top: ${(p) =>
    p.borderTop ? `1px solid ${style.grey_light_1_new}` : 'none'};

  @media ${desktop} {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const SocialContainer = styled(Row)`
  display: flex;
  align-items: center;
  padding-top: 22px;
  border-top: 1px solid ${style.grey_light_1_new};
`;

const PlaysLikesRow = styled(Flex)`
  @media ${desktop} {
    flex-direction: column;
    align-items: flex-start;
  }

  @media ${noDesktop} {
    justify-content: center;
  }
`;

const ButtonsRow = styled(Row)`
  flex-direction: column;
  button {
    width: 100%;
    margin-bottom: 12px;
  }
`;

const FollowersRow = styled(Row)`
  flex-wrap: wrap;
  justify-content: flex-start;
  a {
    margin-right: -6px;
  }

  ${UserImage} {
    border: 2px solid white;
  }
`;

const GenresRow = styled(Row)`
  flex-wrap: wrap;
  justify-content: flex-start;
  a {
    margin-right: 12px;
    margin-top: 6px;
  }
`;

const GreyText = styled(TextRegular14)`
  color: ${style.grey_dark_new};
`;

const CountDescription = styled(TextRegular14)`
  color: ${style.grey_dark_new};
  margin-left: 4px;
`;

const MutualFollowersContainerList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const MutualFollowersContainer = styled.div`
  display: column;
  flex-direction: row;
  align-items: center;

  margin-bottom: 22px;

  a {
    margin-bottom: 15px;
  }

  a:not(:last-child) {
    margin-right: 12px;
  }

  @media ${desktop} {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const ProfileLabelActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 22px;
`;

const Plays = ({ plays }: { plays: number }) => {
  const { t } = useTranslation('profile');
  const isDesktop = useIsDesktop();
  const marginOffset = isDesktop ? -3 : -5;
  return (
    <CounterWrapper>
      <Play size={isDesktop ? 14 : 20} style={{ marginLeft: marginOffset }} />
      <TextBold14 style={{ marginLeft: marginOffset }}>{plays}</TextBold14>{' '}
      <CountDescription> {t('profile:plays')}</CountDescription>
    </CounterWrapper>
  );
};

const Likes = ({ likes }: { likes: number }) => {
  const { t } = useTranslation('profile');
  const isDesktop = useIsDesktop();
  return (
    <CounterWrapper>
      <Heart size={isDesktop ? 10 : 16} />
      <TextBold14>{likes}</TextBold14>{' '}
      <CountDescription> {t('profile:likes')}</CountDescription>
    </CounterWrapper>
  );
};
